
import {useEffect, useState} from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const BarChart = ({datas,labels}) => {
    // Format numbers to K format (thousands)
    const formatToK = (value) => {
        if (value === 0) return '₹0';
        const num = Math.abs(value);
        if (num >= 1000) {
            const rounded = Math.round(num / 1000);
            const sign = value < 0 ? '-' : '';
            return `${sign}₹${rounded}K`;
        }
        // For values less than 1000, show the actual number
        const sign = value < 0 ? '-' : '';
        return `${sign}₹${Math.round(num)}`;
    };

    // Format dates to be shorter and more readable
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        } catch (e) {
            return dateString;
        }
    };

    // Format labels
    const formattedLabels = labels 
        ? labels.map(label => {
            // Check if it's a date string (YYYY-MM-DD format)
            if (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}/.test(label)) {
                return formatDate(label);
            }
            return label;
        })
        : ['January', 'February', 'March', 'April', 'May', 'June'];

    // Create color array based on positive/negative values
    const getBarColors = (values) => {
        return values.map(value => {
            if (value >= 0) {
                // Positive: Use teal gradient
                return '#4DD5B8';
            } else {
                // Negative: Use red/orange
                return '#FF6B6B';
            }
        });
    };

    const chartData = datas || [12, 19, 3, 5, 2, 3];
    const barColors = getBarColors(chartData);

    const data = {
        labels: formattedLabels,
        datasets: [
            {
                label: 'Daily P&L',
                data: chartData,
                backgroundColor: barColors,
                borderColor: barColors,
                borderWidth: 0,
                borderRadius: 4,
                maxBarThickness: 28,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                display: false
            },
            title: {
                display: false,
                text: 'Monthly Sales Data',
            },
            tooltip: {
                backgroundColor: 'rgba(22, 26, 35, 0.9)',
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: '600'
                },
                bodyFont: {
                    size: 12
                },
                borderColor: '#4DD5B8',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function(context) {
                        const value = context.parsed.y;
                        const sign = value >= 0 ? '+' : '';
                        return `P&L: ${sign}₹${value.toLocaleString('en-IN')}`;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: {
                    display: true,
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11,
                        family: "'Outfit', sans-serif"
                    },
                    color: '#6c757d',
                    padding: 8,
                    callback: function(value, index) {
                        const label = this.getLabelForValue(value);
                        // If label is too long, show every nth label
                        if (formattedLabels.length > 10) {
                            return index % Math.ceil(formattedLabels.length / 8) === 0 ? label : '';
                        }
                        return label;
                    }
                },
            },
            y: {
                grid: {
                    display: true,
                    drawBorder: false,
                    color: 'rgba(234, 234, 234, 0.5)',
                    lineWidth: 1
                },
                ticks: {
                    display: true,
                    font: {
                        size: 11,
                        family: "'Outfit', sans-serif"
                    },
                    color: '#6c757d',
                    padding: 8,
                    callback: function(value) {
                        return formatToK(value);
                    }
                },
                border: {
                    display: false
                }
            }
        }
    };
    return <Bar data={data} options={options} />;
};

export default BarChart;
